import { z } from 'zod';

import { isCountryUkraine, YesNoSelectOptions } from '@/features/ui/ProfileChapters';

import {
  emailSchema,
  getAddressSchema,
  getPhoneSchema,
  selectOptionSchema,
  docSerialNumberOptionalSchema120,
  unzrSchema,
  optionalDocDateSchema,
  selectOptionalOptionSchema,
  passportSerialNumberSchema,
  docDateSchema,
  docSerialNumberSchema120,
  docIssueSchema4,
  docSerialSchema9,
  docDateOptionalSchema,
  getAddressSchemaWithSlash,
  passportSerialNumberSchemaEn,
} from '@/components/Forms/Validation';

import { DocumentTypeCode } from '@/api/types';

const commonProfileSchema = z.object({
  // Місце народження
  countryOfBirth: getAddressSchema(100, 2),
  regionOfBirth: getAddressSchema(100),
  districtOfBirth: getAddressSchema(100, 2),
  cityOfBirth: getAddressSchema(100, 2),

  // Зареєстроване місце проживання
  countryOfReg: selectOptionSchema.nullish(),
  regionOfReg: z.any(),
  districtOfReg: z.any(),
  cityOfReg: z.any(),
  streetOfReg: z.any(),
  houseOfReg: getAddressSchemaWithSlash(7),
  buildingOfReg: getAddressSchemaWithSlash(5),
  apartmentOfReg: getAddressSchema(5),

  // Фактичне місце проживання
  actualAddressEqualsResidence: z.boolean(),
  actualCountry: selectOptionSchema.nullish(),
  actualRegion: z.any(),
  actualDistrict: z.any(),
  actualCity: z.any(),
  actualStreet: z.any(),
  actualHouse: getAddressSchemaWithSlash(7),
  actualBuilding: getAddressSchemaWithSlash(5),
  actualApartment: getAddressSchema(5),

  REGISTRATION: z
    .object({
      id: z.string(),
      label: z.string(),
    })
    .nullish(),
  LIVING: z
    .object({
      id: z.string(),
      label: z.string(),
    })
    .nullish(),
});

const baseSchema = commonProfileSchema.extend({
  registeredDocTypeCode: z.nativeEnum(DocumentTypeCode).nullish().optional(),
  actualDocTypeCode: z.nativeEnum(DocumentTypeCode).nullish().optional(),
  documents: z.object({
    REGISTRATION: z.any(),
    LIVING: z.any(),
  }),
});

export const getPassportDocSchema = (type: DocumentTypeCode) => {
  switch (type) {
    case DocumentTypeCode.PASSPORT:
      return z.object({
        info: z.object({
          serialNumberDoc: passportSerialNumberSchema,
          date: docDateSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
    case DocumentTypeCode.ID_CARD:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialSchema9,
          date: docDateSchema,
          expirationDate: docDateSchema,
          auth_dep: docIssueSchema4,
          unzr: unzrSchema,
        }),
      });
    case DocumentTypeCode.INTERNATIONAL_PASSPORT:
      return z.object({
        info: z.object({
          serialNumberDoc: passportSerialNumberSchemaEn,
          date: docDateSchema,
          expirationDate: docDateSchema,
          auth_dep: docIssueSchema4,
          unzr: unzrSchema,
        }),
      });
    case DocumentTypeCode.RESIDENCE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialSchema9,
          date: docDateSchema,
          expirationDate: docDateSchema,
          auth_dep: docIssueSchema4,
          unzr: unzrSchema,
        }),
      });
    case DocumentTypeCode.OLD_RESIDENCE:
      return z.object({
        info: z.object({
          serialNumberDoc: passportSerialNumberSchema,
          auth_dep: docSerialNumberSchema120,
          date: docDateSchema,
        }),
      });
    case DocumentTypeCode.TEMPORARY_RESIDENCE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialSchema9,
          date: docDateSchema,
          expirationDate: docDateSchema,
          auth_dep: docIssueSchema4,
          unzr: unzrSchema,
        }),
      });
  }
};

export const getRequiredRegistrationDocSchema = (type: DocumentTypeCode) => {
  switch (type) {
    case DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          date: docDateSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
  }
};

export const getRegistrationDocSchema = (type: DocumentTypeCode) => {
  switch (type) {
    case DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          date: optionalDocDateSchema,
          auth_dep: docSerialNumberOptionalSchema120,
        }),
      });
  }
};

export const getRequiredLivingDocSchema = (type: DocumentTypeCode) => {
  switch (type) {
    case DocumentTypeCode.CERT_IDP:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          date: docDateSchema,
        }),
      });
    case DocumentTypeCode.LEGAL_RESIDENCE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          auth_dep: docSerialNumberSchema120,
          date: docDateSchema,
          expirationDate: docDateSchema,
        }),
      });
    case DocumentTypeCode.LEASE_AGREEMENT:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          indexNumber: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
          expirationDate: docDateOptionalSchema,
        }),
      });
    case DocumentTypeCode.EXRACT_PRPR:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          indexNumber: docSerialNumberSchema120,
          date: docDateSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
    case DocumentTypeCode.OWNERSHIP_CERTIFICATE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          indexNumber: docSerialNumberSchema120,
          date: docDateSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
    case DocumentTypeCode.CERT_MARRIAGE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          date: docDateSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
  }
};

export const getLivingDocSchema = (type: DocumentTypeCode) => {
  switch (type) {
    case DocumentTypeCode.CERT_IDP:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
        }),
      });
    case DocumentTypeCode.LEGAL_RESIDENCE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          auth_dep: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
          expirationDate: docDateOptionalSchema,
        }),
      });
    case DocumentTypeCode.LEASE_AGREEMENT:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          indexNumber: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
          expirationDate: docDateOptionalSchema,
        }),
      });
    case DocumentTypeCode.EXRACT_PRPR:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          indexNumber: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
          auth_dep: docSerialNumberOptionalSchema120,
        }),
      });
    case DocumentTypeCode.OWNERSHIP_CERTIFICATE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          indexNumber: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
          auth_dep: docSerialNumberOptionalSchema120,
        }),
      });
    case DocumentTypeCode.CERT_MARRIAGE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
          auth_dep: docSerialNumberOptionalSchema120,
        }),
      });
    default:
      return z.any();
  }
};

export const getAdditionalDocumentSchema = (type: DocumentTypeCode) => {
  switch (type) {
    case DocumentTypeCode.PERMIT:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          date: docDateSchema,
          expirationDate: docDateOptionalSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
    case DocumentTypeCode.INSURANCE_POLICY:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
          expirationDate: docDateSchema,
        }),
      });
    case DocumentTypeCode.EXRACT_PRPR:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberOptionalSchema120,
          indexNumber: docSerialNumberOptionalSchema120,
          date: docDateOptionalSchema,
          auth_dep: docSerialNumberOptionalSchema120,
        }),
      });
    case DocumentTypeCode.POWER_OF_ATTORNEY:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          date: docDateSchema,
          expirationDate: docDateSchema,
        }),
      });
    case DocumentTypeCode.EXPERT_RESEARCH:
      return z.object({
        info: z.object({
          date: docDateSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
    case DocumentTypeCode.CERTIFICATION:
      return z.object({
        info: z.object({
          org: docSerialNumberSchema120,
          serialNumberDoc: docSerialNumberSchema120,
          date: docDateSchema,
          expirationDate: docDateOptionalSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
    case DocumentTypeCode.WORK_REFERENCE:
      return z.object({
        info: z.object({
          serialNumberDoc: docSerialNumberSchema120,
          date: docDateSchema,
          expirationDate: docDateOptionalSchema,
          auth_dep: docSerialNumberSchema120,
        }),
      });
  }
};

export const generalDataProfileSchema = z.object({
  lastName: z.string({ message: 'Поле обов’язкове для заповнення' }),
  firstName: z.string({ message: 'Поле обов’язкове для заповнення' }),
  middleName: z.string().optional().nullish(),
  gender: selectOptionalOptionSchema,
  birthday: docDateSchema,
  contactEmail: emailSchema,
  contactPhone: getPhoneSchema(),
});

export const identityCardProfileSchema = z
  .object({
    // Громадянство
    withoutCitizenship: selectOptionSchema,
    citizenship: z.any(),

    passportDocTypeCode: z.nativeEnum(DocumentTypeCode),
    passport: z.object({}, { message: 'Необхідно заповнити поле' }),
    documents: z.object({
      passport: z.any(),
    }),
  })
  .superRefine((data, ctx) => {
    if (data.withoutCitizenship.id === YesNoSelectOptions.NO.id) {
      const citizenshipResult = selectOptionSchema.safeParse(data.citizenship);

      if (!citizenshipResult.success) {
        citizenshipResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['citizenship', ...issue.path],
          });
        });
      }
    }

    const passportSchema = getPassportDocSchema(data.passportDocTypeCode);
    const passportResult = passportSchema?.safeParse(data.documents.passport);
    if (!passportResult?.success) {
      passportResult?.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['documents', 'passport', ...issue.path],
        });
      });
    }
  });

export const profileSchema = baseSchema.superRefine((data, ctx) => {
  // country of reg is not Ukraine
  if (isCountryUkraine(data.countryOfReg?.id)) {
    const cityResult = selectOptionalOptionSchema.safeParse(data.cityOfReg);
    if (!cityResult.success) {
      cityResult.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['cityOfReg', ...issue.path],
        });
      });
    }
    const streetResult = selectOptionalOptionSchema.safeParse(data.streetOfReg);
    if (!streetResult.success) {
      streetResult.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['streetOfReg', ...issue.path],
        });
      });
    }
  } else {
    const cityResult = getAddressSchema(100, 2).nullish().safeParse(data.cityOfReg);
    if (!cityResult.success) {
      cityResult.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['cityOfReg', ...issue.path],
        });
      });
    }
    const streetResult = getAddressSchema(100, 2).nullish().safeParse(data.streetOfReg);
    if (!streetResult.success) {
      streetResult.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['streetOfReg', ...issue.path],
        });
      });
    }
  }

  if (isCountryUkraine(data.countryOfReg?.id)) {
    const regionResult = selectOptionalOptionSchema.safeParse(data.regionOfReg);
    if (!regionResult.success) {
      regionResult.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['regionOfReg', ...issue.path],
        });
      });
    }
  } else {
    const regionResult = getAddressSchema(100, 2).nullish().safeParse(data.regionOfReg);
    if (!regionResult.success) {
      regionResult.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['regionOfReg', ...issue.path],
        });
      });
    }
  }

  if (!data.actualAddressEqualsResidence) {
    if (isCountryUkraine(data.actualCountry?.id)) {
      const regionLivingResult = selectOptionalOptionSchema.safeParse(data.actualRegion);
      if (!regionLivingResult.success) {
        regionLivingResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['actualRegion', ...issue.path],
          });
        });
      }
    } else {
      const regionLivingResult = getAddressSchema(100, 2).nullish().safeParse(data.actualRegion);
      if (!regionLivingResult.success) {
        regionLivingResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['actualRegion', ...issue.path],
          });
        });
      }
    }

    if (isCountryUkraine(data.actualCountry?.id)) {
      const actualCityResult = selectOptionalOptionSchema.safeParse(data.actualCity);
      if (!actualCityResult.success) {
        actualCityResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['actualCity', ...issue.path],
          });
        });
      }
      const actualStreetResult = selectOptionalOptionSchema.safeParse(data.actualStreet);
      if (!actualStreetResult.success) {
        actualStreetResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['actualStreet', ...issue.path],
          });
        });
      }
    } else {
      const actualCityResult = getAddressSchema(100, 2).safeParse(data.actualCity);
      if (!actualCityResult.success) {
        actualCityResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['actualCity', ...issue.path],
          });
        });
      }
      const actualStreetResult = getAddressSchema(100, 2).safeParse(data.actualStreet);
      if (!actualStreetResult.success) {
        actualStreetResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['actualStreet', ...issue.path],
          });
        });
      }
    }
  }

  if (data.registeredDocTypeCode) {
    const registrationSchema = getRegistrationDocSchema(data.registeredDocTypeCode);
    const registrationResult = registrationSchema?.safeParse(data.documents.REGISTRATION);
    if (!registrationResult?.success) {
      registrationResult?.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['documents', 'REGISTRATION', ...issue.path],
        });
      });
    }
  }

  if (data.actualDocTypeCode) {
    const livingSchema = getLivingDocSchema(data.actualDocTypeCode);
    const livingResult = livingSchema.safeParse(data.documents.LIVING);
    if (!livingResult.success) {
      livingResult.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['documents', 'LIVING', ...issue.path],
        });
      });
    }
  }
});
